import { useRouter } from 'expo-router';
import { useNavigation } from 'expo-router/react-navigation';
import React, { useState, useEffect, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { View, Text, Platform } from 'react-native';


import { UserTypeahead } from '../search/user-typeahead';

import { DropboxFolderPicker } from './dropbox-folder-picker';


import { useAlert } from '@/components/alert-provider';
import { DateInput } from '@/components/form/date-input';
import { SecureField } from '@/components/secure/secure-field';
import {
	TextInput, TextInputIcon, Button, useTheme, SegmentedButtons, IconButton, HelperText,
	KeyboardAwareScrollView,
} from '@/components/ui';
import { useDriveFolders } from '@/hooks/use-document-folders';
import { usePermissions } from '@/hooks/use-permissions';
import { dataService } from '@/services/data-service';
import { formStyles as styles } from '@/styles';
import type { Association, User } from '@/types/models';
import { AssociationType } from '@/types/models';
import { businessMsToDate, dueDayMs, toBusinessMs } from '@/utils/date-utils';
import { associationDocumentPath } from '@/utils/document-folder-path';
import { geocodeAddressParts, isValidCoordinates } from '@/utils/geocoding-utils';
import { FormPartial } from '@/utils/partial-type';
import { buildSignature } from '@/utils/unsaved-changes';
import type { FieldSchema } from '@/utils/unsaved-changes';

/** A stored business day (marker ms, or null) → the `Date` a `utcDay` DateInput shows. */
const businessDaySeed = (value: unknown): Date | null => {
	const day = dueDayMs(value);
	return day === null ? null : businessMsToDate(day);
};

interface AssociationFormProps {
	initialValues?: Partial<Association>;
	onSave: (data: FormPartial<Association>) => void;
	onCancel: () => void;
	isSubmitting?: boolean;
	submitButtonText?: string;
	disabled?: boolean;
	/** Hide the built-in Cancel/Save row so a parent page can own saving (e.g.
	 *  a single floating FAB that also persists the dues/late-fee/etc. rules).
	 *  Pair with the `getPayload` imperative handle below. */
	hideActions?: boolean;
	children?: React.ReactNode;
}

/** Imperative handle for parents that drive saving themselves. */
export interface AssociationFormHandle {
	/** Run validation + auto-geocode and return the payload, or null when the
	 *  form is invalid (required fields missing). */
	getPayload: () => Promise<FormPartial<Association> | null>;
}

export const AssociationForm = forwardRef<AssociationFormHandle, AssociationFormProps>(({
	initialValues = {},
	onSave,
	onCancel,
	isSubmitting = false,
	submitButtonText = 'Save',
	disabled = false,
	hideActions = false,
	children,
}, formRef) => {
	const theme = useTheme();
	/**
	 * The Dropbox folder override is `MANAGE_DOCUMENT_STRUCTURE` — the Admin role or the
	 * `association_admin` capability.
	 *
	 * It decides where a whole community's documents are read from AND written to, so a wrong
	 * value does not error, it silently detaches the community from its filing.
	 *
	 * The matching WRITE guard lives in `api/associations.ts`, and `/documents/folders` (the
	 * picker's source) is gated on the same permission. They must agree: the server DROPS an
	 * unauthorised `dropboxPath` rather than refusing it, so a field shown to someone it does
	 * not accept looks saved and is not.
	 */
	const { canManageDocumentStructure: canPinDropboxPath } = usePermissions();

	// Navigation + alerts for unsaved changes
	const navigation = useNavigation();
	const router = useRouter();
	const { alert } = useAlert();
	// Treat as create when there is no id
	const isNew = !initialValues?.id;

	// Form state
	const [name, setName] = useState(initialValues.name || '');
	const [address, setAddress] = useState(initialValues.address || '');
	const [city, setCity] = useState(initialValues.city || '');
	const [state, setState] = useState(initialValues.state || '');
	const [zipCode, setZipCode] = useState(initialValues.zipCode || '');
	const [lat, setLat] = useState<number | undefined>(initialValues.lat);
	const [lng, setLng] = useState<number | undefined>(initialValues.lng);
	const [assignedTo, setAssignedTo] = useState<User | null>(null);
	const [type, setType] = useState<AssociationType>(initialValues.type || AssociationType.HOA);
	const [contactName, setContactName] = useState(initialValues.contactName || '');
	const [contactEmail, setContactEmail] = useState(initialValues.contactEmail || '');
	const [contactPhone, setContactPhone] = useState(initialValues.contactPhone || '');
	const [numberOfUnits, setNumberOfUnits] = useState<string>(initialValues.numberOfUnits?.toString() || '');
	const [masterAssociation, setMasterAssociation] = useState(initialValues.masterAssociation || '');
	// Encrypted EIN (Secure Fields): `ein` holds the `sf1:` ciphertext, `einMask`
	// the plaintext display mask. SecureField produces both on save.
	const [ein, setEin] = useState(initialValues.ein ?? '');
	const [einMask, setEinMask] = useState<string | null>(initialValues.einMask ?? null);
	// Explicit Dropbox folder override — SUPER ADMIN only (migration 247). '' means "derive
	// from the name", which is what all but a handful of associations should be.
	const [dropboxPath, setDropboxPath] = useState(initialValues.dropboxPath ?? '');
	const [folderPickerOpen, setFolderPickerOpen] = useState(false);
	// Management contract dates (#417) — business days, edited as UTC-day `DateInput`s.
	const [startDate, setStartDate] = useState<Date | null>(() => businessDaySeed(initialValues.startDate));
	const [terminationDate, setTerminationDate] = useState<Date | null>(
		() => businessDaySeed(initialValues.terminationDate),
	);

	// Geocoding state
	const [isGeocoding, setIsGeocoding] = useState(false);
	const [geocodingError, setGeocodingError] = useState<string | null>(null);

	// Unsaved changes signature schema
	const sigSchema: FieldSchema = useMemo(() => ({
		name: 'string',
		address: 'string',
		city: 'string',
		state: 'string',
		zipCode: 'string',
		lat: 'number',
		lng: 'number',
		contactName: 'string',
		contactEmail: 'string',
		contactPhone: 'string',
		assignedToId: 'id',
		type: 'string',
		numberOfUnits: 'number',
		ein: 'string',
		masterAssociation: 'string',
		dropboxPath: 'string',
		startDate: 'date',
		terminationDate: 'date',
	}), []);
	const [lastSavedSig, setLastSavedSig] = useState<string>('');
	const currentSig = useMemo(() => buildSignature({
		name,
		address,
		city,
		state,
		zipCode,
		lat,
		lng,
		contactName,
		contactEmail,
		contactPhone,
		assignedToId: assignedTo?.id,
		type,
		numberOfUnits: numberOfUnits ? parseInt(numberOfUnits, 10) : undefined,
		ein,
		masterAssociation,
		dropboxPath,
		startDate,
		terminationDate,
	}, sigSchema), [name, address, city, state, zipCode, lat, lng, contactName, contactEmail, contactPhone, assignedTo, type, numberOfUnits, ein, masterAssociation, dropboxPath, startDate, terminationDate, sigSchema]);
	const hasUnsavedChanges = useCallback(() => currentSig !== lastSavedSig, [currentSig, lastSavedSig]);

	// Disabled unsaved changes navigation prevention
	// useEffect(() => {
	// 	if (isNew) return;
	// 	const originalPush = router.push;
	// 	const originalReplace = router.replace;
	// 	const originalBack = router.back;
	// 	const originalNavigate = router.navigate;

	// 	const checkAndGo = (fn: (...args: any[]) => void, ...args: any[]) => {
	// 		// Allow navigation while submitting, or when there are no unsaved changes
	// 		if (isSubmitting || !hasUnsavedChanges()) return fn.apply(router, args);
	// 		alert(
	// 			'Unsaved Changes',
	// 			'You have unsaved changes. Are you sure you want to leave without saving?',
	// 			[
	// 				{ text: 'Stay', style: 'cancel' },
	// 				{ text: 'Leave', style: 'destructive', onPress: () => fn.apply(router, args) },
	// 			],
	// 		);
	// 	};

	// 	router.push = (...args: any[]) => checkAndGo(originalPush, ...args);
	// 	router.replace = (...args: any[]) => checkAndGo(originalReplace, ...args);
	// 	router.back = (...args: any[]) => checkAndGo(originalBack, ...args);
	// 	router.navigate = (...args: any[]) => checkAndGo(originalNavigate, ...args);

	// 	return () => {
	// 		router.push = originalPush;
	// 		router.replace = originalReplace;
	// 		router.back = originalBack;
	// 		router.navigate = originalNavigate;
	// 	};
	// }, [router, hasUnsavedChanges, alert, isSubmitting, isNew]);

	// Web beforeunload
	useEffect(() => {
		if (isNew) return;
		if (Platform.OS !== 'web') return;
		const handler = (e: BeforeUnloadEvent) => {
			if (isSubmitting || !hasUnsavedChanges()) return;
			e.preventDefault();
			e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
			return 'You have unsaved changes. Are you sure you want to leave?';
		};
		window.addEventListener('beforeunload', handler);
		return () => window.removeEventListener('beforeunload', handler);
	}, [hasUnsavedChanges, isSubmitting, isNew]);

	// Reset form state when initialValues changes (only for edit; creates shouldn't reset on render)
	useEffect(() => {
		if (isNew) return;
		if (initialValues) {
			setName(initialValues.name || '');
			setAddress(initialValues.address || '');
			setCity(initialValues.city || '');
			setState(initialValues.state || '');
			setZipCode(initialValues.zipCode || '');
			setLat(initialValues.lat);
			setLng(initialValues.lng);
			setContactName(initialValues.contactName || '');
			setContactEmail(initialValues.contactEmail || '');
			setContactPhone(initialValues.contactPhone || '');
			setType(initialValues.type || AssociationType.HOA);
			setNumberOfUnits(initialValues.numberOfUnits?.toString() || '');
			setMasterAssociation(initialValues.masterAssociation || '');
			setEin(initialValues.ein ?? '');
			setEinMask(initialValues.einMask ?? null);
			setDropboxPath(initialValues.dropboxPath ?? '');
			setStartDate(businessDaySeed(initialValues.startDate));
			setTerminationDate(businessDaySeed(initialValues.terminationDate));

			// Load assigned user if available
			if (initialValues.assignedTo) {
				// If assignedTo is already a User object, use it directly
				if (typeof initialValues.assignedTo === 'object' && 'id' in initialValues.assignedTo) {
					setAssignedTo(initialValues.assignedTo as User);
				} else {
					// If it's a string ID, fetch the user
					dataService.getUser(initialValues.assignedTo as string).then((user) => {
						setAssignedTo(user);
					}).catch(() => {
						setAssignedTo(null);
					});
				}
			} else {
				setAssignedTo(null);
			}

			// Initialize last-saved signature from incoming values
			const initSig = buildSignature({
				name: initialValues.name,
				address: initialValues.address,
				city: initialValues.city,
				state: initialValues.state,
				zipCode: initialValues.zipCode,
				lat: initialValues.lat,
				lng: initialValues.lng,
				contactName: initialValues.contactName,
				contactEmail: initialValues.contactEmail,
				contactPhone: initialValues.contactPhone,
				assignedToId: initialValues.assignedTo,
				type: initialValues.type || AssociationType.HOA,
				numberOfUnits: initialValues.numberOfUnits,
				ein: initialValues.ein,
				masterAssociation: initialValues.masterAssociation,
				dropboxPath: initialValues.dropboxPath ?? '',
				startDate: businessDaySeed(initialValues.startDate),
				terminationDate: businessDaySeed(initialValues.terminationDate),
				notes: initialValues.notes || '',
			}, sigSchema);
			setLastSavedSig(initSig);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only resets on initialValues change, not isNew
	}, [initialValues, sigSchema]);

	// Validation state
	const datesOutOfOrder = startDate !== null && terminationDate !== null
		&& toBusinessMs(terminationDate) < toBusinessMs(startDate);
	const isValid = name.trim() && type && !datesOutOfOrder;

	/**
	 * What the folder would be with no override — shown as the field's placeholder and its
	 * "derived from the name" helper.
	 *
	 * Recomputed from the LIVE name and type rather than the saved row, so renaming an
	 * association shows immediately where its documents are about to be looked for. That is
	 * the moment someone is most likely to need a pin: a rename relocates the derived folder
	 * while the folder on the drive stays exactly where it was (#373).
	 */
	const derivedDocumentPath = useMemo(() => associationDocumentPath({
		name,
		type,
		archivedAt: initialValues.archivedAt,
		status: initialValues.status,
	}), [name, type, initialValues.archivedAt, initialValues.status]);

	/**
	 * Does the pinned folder actually exist on the drive?
	 *
	 * NON-BLOCKING by design — this is a warning, never a validation error. The folder may be
	 * one that is about to be created, and refusing to save would make the field unusable for
	 * exactly that case. Same list the picker uses, so the dropdown cannot offer a path this
	 * then rejects.
	 *
	 * Silent while loading and on error: "that folder isn't on the drive" is a claim, and
	 * making it from an empty list would accuse every correct pin of being wrong.
	 */
	const { data: driveFolders } = useDriveFolders(canPinDropboxPath && !isNew);
	const folderWarning = useMemo(() => {
		const pin = dropboxPath.trim();
		if (!pin || !driveFolders?.folders?.length) return null;
		const known = driveFolders.folders.some((f) => f.path.toLowerCase() === pin.toLowerCase());
		return known ? null : 'No folder at this path on the drive yet — documents will 404 until it exists.';
	}, [dropboxPath, driveFolders]);

	// Geocoding functions
	const performGeocoding = async (): Promise<boolean> => {
		setIsGeocoding(true);
		setGeocodingError(null);

		try {
			const addressParts = {
				address: address.trim() || undefined,
				city: city.trim() || undefined,
				state: state.trim() || undefined,
				zipCode: zipCode.trim() || undefined,
			};

			const result = await geocodeAddressParts(addressParts);

			if (result) {
				setLat(result.latitude);
				setLng(result.longitude);
				return true;
			} else {
				setGeocodingError('Could not find coordinates for this address');
				return false;
			}
		} catch (error) {
			console.error('Geocoding error:', error);
			setGeocodingError('Failed to geocode address');
			return false;
		} finally {
			setIsGeocoding(false);
		}
	};

	const handleManualGeocode = async () => {
		await performGeocoding();
	};

	// Runs validation + auto-geocode and returns the payload (or null when the
	// form is invalid). Shared by the built-in Save button and the imperative
	// `getPayload` handle a parent FAB uses to drive saving.
	const buildPayload = async (): Promise<FormPartial<Association> | null> => {
		if (!isValid) return null;

		// Auto-geocode if we have address fields but no coordinates
		const hasAddressFields = address.trim() || city.trim() || state.trim() || zipCode.trim();
		const hasCoordinates = isValidCoordinates(lat, lng);

		if (hasAddressFields && !hasCoordinates) {
			console.log('Auto-geocoding address...');
			await performGeocoding();
		}

		const payload: FormPartial<Association> = {
			name: name.trim(),
			address: address.trim(),
			city: city.trim(),
			state: state.trim(),
			zipCode: zipCode.trim() || undefined,
			lat,
			lng,
			contactName: contactName.trim() || undefined,
			contactEmail: contactEmail.trim() || undefined,
			contactPhone: contactPhone.trim() || undefined,
			assignedTo: assignedTo?.id,
			type,
			numberOfUnits: numberOfUnits ? parseInt(numberOfUnits, 10) : undefined,
			masterAssociation: masterAssociation.trim() || undefined,
			// SecureField has already encrypted these; persist verbatim.
			ein,
			einMask: einMask ?? undefined,
			/**
			 * Sent only by a super admin, and `null` rather than `undefined` when cleared.
			 *
			 * The distinction is load-bearing at both ends. `JSON.stringify` DROPS an
			 * `undefined` value, so the server would see no key and leave the pin untouched —
			 * which is right for a user who may not change it, and wrong for a super admin who
			 * just emptied the field. `null` is how "clear this" survives the wire.
			 *
			 * The server refuses it independently (`api/associations.ts`); omitting it here
			 * keeps a field nobody can change out of the unsaved-changes signature and off the
			 * request, rather than relying on the gate alone.
			 */
			...(canPinDropboxPath ? { dropboxPath: dropboxPath.trim() || null } : {}),
			// `null`, not `undefined`, when cleared — same wire reason as `dropboxPath` above.
			startDate: startDate ? toBusinessMs(startDate) : null,
			terminationDate: terminationDate ? toBusinessMs(terminationDate) : null,
		};
		const savedSig = buildSignature({
			name: payload.name,
			address: payload.address,
			city: payload.city,
			state: payload.state,
			zipCode: payload.zipCode,
			lat: payload.lat,
			lng: payload.lng,
			contactName: payload.contactName,
			contactEmail: payload.contactEmail,
			contactPhone: payload.contactPhone,
			assignedToId: payload.assignedTo,
			type: payload.type,
			numberOfUnits: payload.numberOfUnits,
			ein: payload.ein,
			masterAssociation: payload.masterAssociation,
			// The STATE, not `payload.dropboxPath` — the payload carries null for a cleared
			// field, while the signature is built from '' everywhere else. Mixing the two makes
			// a just-saved form read as still dirty.
			dropboxPath,
			startDate,
			terminationDate,
		}, sigSchema);
		// Update last-saved sig so navigation after save is not blocked. Only on
		// edit pages; on create we keep fields as-is.
		if (!isNew) setLastSavedSig(savedSig);
		return payload;
	};

	const handleSubmit = async () => {
		const payload = await buildPayload();
		if (payload) onSave(payload);
	};

	useImperativeHandle(formRef, () => ({ getPayload: buildPayload }));

	const handleLocationChange = (lat: number, lng: number) => {
		setLat(lat);
		setLng(lng);
	};

	return (
		<KeyboardAwareScrollView style={styles.container} extraScrollHeight={100} testID="association-form">
			<TextInput
				mode="outlined"
				label="Name *"
				value={name}
				onChangeText={setName}
				style={styles.input}
				disabled={disabled}
				testID="association-name-input"
			/>
			<TextInput
				mode="outlined"
				label="Address"
				value={address}
				onChangeText={setAddress}
				style={styles.input}
				disabled={disabled}
				testID="association-address-input"
			/>
			<TextInput
				mode="outlined"
				label="City"
				value={city}
				onChangeText={setCity}
				style={styles.input}
				disabled={disabled}
				testID="association-city-input"
			/>
			<TextInput
				mode="outlined"
				label="State"
				value={state}
				onChangeText={setState}
				style={styles.input}
				disabled={disabled}
				testID="association-state-input"
			/>
			<TextInput
				mode="outlined"
				label="ZIP Code"
				value={zipCode}
				onChangeText={setZipCode}
				style={styles.input}
				disabled={disabled}
				testID="association-zip-input"
			/>

			{/* Location/Geocoding Section */}
			<View style={styles.input}>
				<View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
					<Text style={{ fontSize: 16, fontWeight: '500' }}>Location</Text>
					<IconButton
						icon="location-outline"
						size={20}
						onPress={handleManualGeocode}
						disabled={disabled || isGeocoding}
					/>
				</View>

				{isValidCoordinates(lat, lng) ? (
					<View style={{ backgroundColor: theme.colors.surfaceVariant, padding: 8, borderRadius: 4 }}>
						<Text style={{ fontSize: 12, color: theme.colors.onSurfaceVariant }}>
							Coordinates: {lat?.toFixed(5)}, {lng?.toFixed(5)}
						</Text>
					</View>
				) : (
					<Text style={{ fontSize: 12, color: theme.colors.onSurfaceVariant, fontStyle: 'italic' }}>
						No coordinates set. Use the location button to geocode the address.
					</Text>
				)}

				{geocodingError && (
					<Text style={{ fontSize: 12, color: theme.colors.error, marginTop: 4 }}>
						{geocodingError}
					</Text>
				)}
			</View>
			<TextInput
				mode="outlined"
				label="Number of Units"
				value={numberOfUnits}
				onChangeText={setNumberOfUnits}
				keyboardType="numeric"
				style={styles.input}
				disabled={disabled}
				testID="association-units-input"
			/>
			<TextInput
				mode="outlined"
				label="Master Association"
				value={masterAssociation}
				onChangeText={setMasterAssociation}
				style={styles.input}
				disabled={disabled}
				placeholder="Name of the master association this rolls up to"
				testID="association-master-association-input"
			/>
			<View style={[styles.input, { flexDirection: 'row', gap: 12 }]}>
				<View style={{ flex: 1 }}>
					<DateInput
						label="Start Date"
						value={startDate}
						onChange={setStartDate}
						disabled={disabled}
						utcDay
						testID="association-start-date-input"
					/>
				</View>
				<View style={{ flex: 1 }}>
					<DateInput
						label="Termination Date"
						value={terminationDate}
						onChange={setTerminationDate}
						disabled={disabled}
						error={datesOutOfOrder ? 'Termination date is before the start date' : undefined}
						utcDay
						testID="association-termination-date-input"
					/>
				</View>
			</View>

			<View style={styles.input}>
				<SecureField
					label="EIN"
					value={ein}
					storedMask={einMask}
					maskStrategy="taxId"
					disabled={disabled}
					onChange={({ ciphertext, mask }) => { setEin(ciphertext); setEinMask(mask); }}
					placeholder="12-3456789"
					helperText="Stored encrypted; only visible after entering the sensitive-data password."
				/>
			</View>

			{/* Contact Information
			<View style={styles.input}>
				<Text style={{ marginBottom: 8, fontSize: 16, fontWeight: '500' }}>Contact Information</Text>
			</View>
			<TextInput
				mode="outlined"
				label="Contact Name"
				value={contactName}
				onChangeText={setContactName}
				style={styles.input}
				disabled={disabled}
				testID="association-contact-name-input"
			/>
			<TextInput
				mode="outlined"
				label="Contact Email"
				value={contactEmail}
				onChangeText={setContactEmail}
				keyboardType="email-address"
				autoCapitalize="none"
				style={styles.input}
				disabled={disabled}
				testID="association-contact-email-input"
			/>
			<TextInput
				mode="outlined"
				label="Contact Phone"
				value={contactPhone}
				onChangeText={setContactPhone}
				keyboardType="phone-pad"
				style={styles.input}
				disabled={disabled}
				testID="association-contact-phone-input"
			/>
			 */}

			<UserTypeahead
				label="Property Manager"
				value={assignedTo || null}
				onSelect={(user) => {
					setAssignedTo(user);
				}}
				placeholder="Select a property manager"
				testID="association-property-manager-input"
			/>

			<View style={styles.input}>
				<Text style={{ marginBottom: 8 }}>Type *</Text>
				<View testID="association-type-select">
					<SegmentedButtons
						value={type}
						onValueChange={(value) => setType(value as AssociationType)}
						buttons={[
							{ value: AssociationType.HOA, label: 'HOA' },
							{ value: AssociationType.Condo, label: 'Condo' },
						]}
					/>
				</View>
			</View>

			{/**
			 * Dropbox folder override — super admin only, and deliberately last.
			 *
			 * Almost every association should leave this empty: the folder is derived from the
			 * name, and casing differences are handled without any help. It is here for the
			 * handful whose drive folder is named something the derivation cannot reach.
			 */}
			{canPinDropboxPath && !isNew && (
				<View style={styles.input}>
					<TextInput
						mode="outlined"
						label="Dropbox Path (advanced)"
						value={dropboxPath}
						onChangeText={setDropboxPath}
						autoCapitalize="none"
						placeholder={derivedDocumentPath}
						disabled={disabled}
						testID="association-dropbox-path-input"
						right={(
							<TextInputIcon
								icon="folder-open-outline"
								onPress={() => setFolderPickerOpen(true)}
								testID="association-dropbox-path-browse"
							/>
						)}
					/>
					<HelperText type={folderWarning ? 'error' : 'info'} visible>
						{folderWarning ?? (dropboxPath.trim()
							? 'Documents are read from and written to this folder.'
							: `Derived from the name: ${derivedDocumentPath}`)}
					</HelperText>
				</View>
			)}

			<DropboxFolderPicker
				visible={folderPickerOpen}
				onDismiss={() => setFolderPickerOpen(false)}
				onSelect={setDropboxPath}
				current={dropboxPath || null}
			/>

			{!hideActions && (
				<View style={styles.buttonContainer}>
					<Button
						mode="outlined"
						onPress={onCancel}
						style={styles.button}
						textColor={theme.colors.onSurfaceVariant}
						testID="association-cancel-button"
					>
						<Text>Cancel</Text>
					</Button>
					<Button
						mode="contained"
						onPress={handleSubmit}
						loading={isSubmitting}
						disabled={isSubmitting || !isValid || disabled}
						style={styles.button}
						testID="association-save-button"
					>
						{submitButtonText}
					</Button>
				</View>
			)}
			{children}
		</KeyboardAwareScrollView>
	);
});

AssociationForm.displayName = 'AssociationForm';
